'use client';

import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

import { HorizonOverlay } from './PropertyCard';
import { FormatPrice } from '@/components/ui/FormatPrice';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';

// Structured location from the Route Royal API
interface PropertyLocation {
  city?: string | null;
  suburb?: string | null;
  district?: string | null;
  neighbourhood?: string | null;
  region?: string | null;
  country?: string | null;
  countryCode?: string | null;
  countryName?: string | null;
  localityName?: string | null;
  administrativeAreaName?: string | null;
}

interface Property {
  id: string;
  name: string;
  description?: string | null;
  status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED';
  primaryImageUrl?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  totalFloorArea?: number | null;
  totalFloorAreaUnit?: string | null;
  currency?: string | null;
  location?: PropertyLocation | null;
  attributeValues?: {
    askingPrice?: {
      valueNumber?: number | null;
      valueString?: string;
    };
  } | null;
}

interface FormattedProperty {
  id: string;
  image: string;
  name: string;
  location: string;
  bedrooms: number;
  area: string | null;
  askingPrice: number | null;
  currency: string;
  isFrance: boolean;
  countryLabel: string;
}

interface SpotlightCardProps {
  property: FormattedProperty;
  index: number;
  locale: string;
  translations?: Record<string, string>;
}

function SpotlightPropertyCard({ property, index, locale, translations = {} }: SpotlightCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLAnchorElement>(null);
  const tt = (key: string, fallback: string): string => translations[key] || fallback;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const staggerDelay = index * 150;

  return (
    <Link 
      ref={cardRef}
      href={`/${locale}/properties/${property.id}`}
      className="group min-w-[85vw] md:min-w-[55vw] lg:min-w-[45vw] snap-center flex flex-col gap-5"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 800ms cubic-bezier(0.22, 1, 0.36, 1) ${staggerDelay}ms, transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${staggerDelay}ms`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className="relative w-full aspect-[4/3] overflow-hidden bg-stone-100">
        <Image 
          src={property.image}
          alt={property.name}
          fill
          className={`object-cover transition-all duration-[2000ms] ease-out will-change-transform ${
            isHovered ? 'scale-[1.06] brightness-[0.92]' : 'scale-100 brightness-100'
          }`}
          sizes="(max-width: 768px) 85vw, (max-width: 1024px) 55vw, 45vw"
        />
        
        <HorizonOverlay isHovered={isHovered} isFrance={property.isFrance} />
        
        {/* Center Floating "View" Button */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
          <div 
            className={`relative flex items-center justify-center transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
              isHovered ? 'w-24 h-24 opacity-100' : 'w-12 h-12 opacity-0 translate-y-4'
            }`}
          >
            <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
            <span className="text-white font-serif italic text-xl tracking-widest drop-shadow-md">
              {tt('view', 'View')}
            </span>
          </div>
        </div>
        
        {/* Bottom gradient + location label on hover */}
        <div 
          className={`absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/70 via-black/30 to-transparent px-6 pb-6 pt-16 transition-opacity duration-700 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <p 
            className={`text-[11px] uppercase tracking-[0.2em] text-white/80 font-sans font-medium transition-all duration-1000 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              isHovered ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            {property.location}
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-4 relative">
        {/* Divider line with draw-in animation */}
        <div className="relative h-px bg-stone-200 overflow-hidden">
          <div 
            className={`absolute inset-y-0 left-0 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              property.isFrance ? 'bg-blue-900/30' : 'bg-teal-800/30'
            }`}
            style={{ width: isHovered ? '100%' : '0%' }}
          />
        </div>

        <div className="flex justify-between items-baseline pt-4">
          <h3 className={`font-serif text-xl md:text-2xl text-stone-900 leading-none transition-all duration-500 ${isHovered ? 'italic' : ''}`}>
            {property.name}
          </h3>
          {property.askingPrice && property.askingPrice > 0 && (
            <span className="font-sans text-xs font-medium text-stone-600 shrink-0 ml-4">
              <FormatPrice
                amount={property.askingPrice}
                currency={property.currency}
                size="sm"
                className="!text-stone-600"
              />
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 text-stone-400 font-sans text-[10px] tracking-[0.2em] uppercase overflow-hidden">
          {property.bedrooms > 0 && (
            <span 
              className="transition-all duration-500 ease-out"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
                transitionDelay: `${staggerDelay + 200}ms`,
              }}
            >
              {property.bedrooms} {tt('beds', 'Beds')}
            </span>
          )}
          {property.bedrooms > 0 && property.area && <span className="w-px h-2.5 bg-stone-300" aria-hidden="true" />}
          {property.area && (
            <span
              className="transition-all duration-500 ease-out"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
                transitionDelay: `${staggerDelay + 300}ms`,
              }}
            >
              {property.area} m²
            </span>
          )}
          <span className="w-px h-2.5 bg-stone-300" aria-hidden="true" />
          {property.countryLabel && (
            <span 
              className={`transition-all duration-500 ${property.isFrance ? 'group-hover:text-blue-900' : 'group-hover:text-teal-800'}`}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
                transitionDelay: `${staggerDelay + 400}ms`,
              }}
            >
              {property.countryLabel}
            </span>
          )}
        </div>
        
        {/* Arrow indicator on hover */}
        <div className={`absolute right-0 bottom-0 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
          <ArrowUpRight size={20} className="text-stone-900" strokeWidth={1} />
        </div>
      </div>
    </Link>
  );
}

/**
 * Build a display-ready location label from structured API location data.
 * Uses the actual location relation returned by the API -- no string matching.
 */
function buildLocationLabel(location: PropertyLocation | null | undefined): string {
  if (!location) return '';

  const city = location.city || location.localityName || location.suburb || location.neighbourhood;
  const region = location.region || location.administrativeAreaName || location.district;
  const country = location.country || location.countryName;

  if (city && country) return `${city}, ${country}`;
  if (region && country) return `${region}, ${country}`;
  if (city && region) return `${city}, ${region}`;
  if (country) return country;
  if (city) return city;
  if (region) return region;

  return '';
}

/**
 * Determine whether a property is in France using structured location data.
 * Checks countryCode and countryName directly -- no heuristics.
 */
function resolveIsFrance(location: PropertyLocation | null | undefined): boolean {
  if (!location) return false;

  const code = (location.countryCode || '').toLowerCase();
  if (code === 'fr') return true;

  const country = (location.country || location.countryName || '').toLowerCase();
  return country === 'france';
}

function formatPropertyForDisplay(property: Property, locale?: string): FormattedProperty {
  const askingPrice = property.attributeValues?.askingPrice?.valueNumber ?? null;
  const currency = property.currency || DEFAULT_CURRENCY;
  const area = property.totalFloorArea 
    ? `${property.totalFloorArea}`
    : null;

  const location = buildLocationLabel(property.location);
  const isFrance = resolveIsFrance(property.location);
  const loc = property.location;
  let countryLabel = loc?.country || loc?.countryName || '';

  if (locale === 'fr' || locale === 'es') {
    const lower = countryLabel.toLowerCase();
    if (lower === 'mauritius' || lower === 'île maurice' || lower === 'ile maurice') {
      countryLabel = 'Ile Maurice';
    }
  }

  return {
    id: property.id,
    image: property.primaryImageUrl || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop',
    name: property.name,
    location,
    bedrooms: property.bedrooms || 0,
    area,
    askingPrice,
    currency,
    isFrance,
    countryLabel,
  };
}

export interface StoneInvestmentSignatureSelectionProps {
  properties: Property[];
  onPropertyClick?: (property: Property) => void;
  maxItems?: number;
  locale?: string;
  title?: string;
  eyebrow?: string;
  messages?: Record<string, unknown>;
}

/**
 * Stone Investment - Signature Selection
 * 
 * Horizontal scroll carousel with staggered entrance animations,
 * scroll-edge fade hints, and animated section header.
 */
export function StoneInvestmentSignatureSelection({
  properties,
  onPropertyClick: _onPropertyClick,
  maxItems = 3,
  locale = 'en',
  title: titleProp = 'Signature Selection',
  eyebrow: eyebrowProp = 'Curated Highlights',
  messages,
}: StoneInvestmentSignatureSelectionProps) {
  const stoneMessages = (messages?.stone || {}) as Record<string, unknown>;
  const selectionMessages = (stoneMessages.signatureSelection || {}) as Record<string, string>;

  const title = selectionMessages.title || titleProp;
  const eyebrow = selectionMessages.eyebrow || eyebrowProp;

  const formattedProperties = useMemo(() => 
    properties.slice(0, maxItems).map(p => formatPropertyForDisplay(p, locale)), 
    [properties, maxItems, locale]
  );

  const [headerVisible, setHeaderVisible] = useState(false);
  const headerRef = useRef<HTMLDivElement>(null);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) { setHeaderVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  if (formattedProperties.length === 0) {
    return null;
  }

  return (
    <section className="w-full pb-24 overflow-hidden bg-stone-50">
      {/* Header with entrance animation */}
      <div ref={headerRef} className="container mx-auto px-6 md:px-12 mb-12 flex justify-between items-end">
        <div className="overflow-hidden">
          <span 
            className="block text-stone-400 uppercase tracking-[0.25em] text-xs font-sans font-medium"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(100%)',
              transition: 'opacity 600ms cubic-bezier(0.22, 1, 0.36, 1), transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {eyebrow}
          </span>
          {/* Decorative line that draws in */}
          <div className="overflow-hidden my-3">
            <div 
              className="h-px bg-stone-300"
              style={{
                width: headerVisible ? '48px' : '0px',
                transition: 'width 800ms cubic-bezier(0.22, 1, 0.36, 1) 200ms',
              }}
            />
          </div>
          <h3 
            className="font-serif text-2xl md:text-3xl text-stone-900"
            style={{
              opacity: headerVisible ? 1 : 0,
              transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms, transform 600ms cubic-bezier(0.22, 1, 0.36, 1) 300ms',
            }}
          >
            {title}
          </h3>
        </div>
        <div 
          className="hidden md:flex items-center gap-3 text-stone-400 text-xs uppercase tracking-[0.2em] font-sans"
          style={{
            opacity: headerVisible ? 1 : 0,
            transition: 'opacity 600ms cubic-bezier(0.22, 1, 0.36, 1) 500ms',
          }}
        >
          <span>{selectionMessages.scroll || 'Scroll'}</span>
          <ArrowRight size={14} className="animate-[nudgeRight_2s_ease-in-out_infinite]" aria-hidden="true" />
        </div>
      </div>
      
      {/* Horizontal Scroll Carousel with edge fades */}
      <div className="relative">
        {/* Left fade */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-stone-50 to-transparent z-10 pointer-events-none transition-opacity duration-300"
          style={{ opacity: canScrollLeft ? 1 : 0 }}
        />
        {/* Right fade */}
        <div 
          className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-stone-50 to-transparent z-10 pointer-events-none transition-opacity duration-300"
          style={{ opacity: canScrollRight ? 1 : 0 }}
        />

        <div 
          ref={scrollContainerRef}
          className="flex overflow-x-auto gap-8 px-6 md:px-12 pb-10 snap-x snap-mandatory no-scrollbar"
        >
          {formattedProperties.map((property, idx) => (
            <SpotlightPropertyCard 
              key={property.id} 
              property={property}
              index={idx}
              locale={locale}
              translations={selectionMessages}
            />
          ))}
          <div className="min-w-[8vw] shrink-0" />
        </div>
      </div>

      {/* Custom keyframes for the scroll arrow nudge */}
      <style jsx global>{`
        @keyframes nudgeRight {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(6px); }
        }
      `}</style>
    </section>
  );
}
